// web/src/preferences/LanguageSelect.tsx
//
// Settings card body for choosing UI language. Mirrors ThemeSelect's
// RadioGroup pattern. Native names are shown so the operator can pick
// their language even if the current UI is in a language they don't read.
//
// Side effects on selection (in order):
//   1. setLanguage(code) — flips i18next + localStorage["language"] +
//      <html lang/dir> via the LanguageProvider.
//   2. PATCH /api/v1/me { preferred_language: code } — persists to the
//      server so the next session resolves the same language even on a
//      fresh device. On failure (M10) we toast the error AND revert the
//      UI back to the previous language so the visible state matches
//      what the server actually has.
//   3. Invalidate the "me" query so any reader of preferred_language picks
//      up the new value.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { languagesApi } from "@/api/languages";
import { meApi } from "@/api/me";
import { useLanguage } from "@/i18n/useLanguage";

export function LanguageSelect() {
  const { t } = useTranslation("preferences");
  const { language, setLanguage } = useLanguage();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["languages"],
    queryFn: () => languagesApi.list(),
  });

  if (isLoading || !data) {
    return (
      <div className="text-sm text-muted-foreground">{t("language.loading")}</div>
    );
  }

  return (
    <RadioGroup
      value={language}
      onValueChange={async (code) => {
        const previous = language;
        await setLanguage(code);
        try {
          await meApi.patch({ preferred_language: code });
        } catch (err) {
          // M10: PATCH failure used to be a silent `catch {}` — the UI
          // showed the new language but the server still had the old.
          // Surface the failure with a toast AND revert to the previous
          // language so the displayed state matches the source of truth.
          // The catalog still loads for the reverted language because
          // i18n keeps EN-fallback for any missing namespace.
          const message =
            err instanceof Error && err.message
              ? err.message
              : t("language.saveFailedDefault");
          toast.error(t("language.saveFailed", { message }));
          await setLanguage(previous);
        }
        await qc.invalidateQueries({ queryKey: ["me"] });
      }}
      className="grid grid-cols-1 gap-2 sm:grid-cols-3"
    >
      {data.languages.map((lang) => (
        <Label
          key={lang.code}
          htmlFor={`language-${lang.code}`}
          className="flex items-center gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-muted has-[input:checked]:border-primary has-[input:checked]:bg-accent"
        >
          <RadioGroupItem
            id={`language-${lang.code}`}
            value={lang.code}
            className="sr-only"
          />
          <span className="flex-1">
            <span className="block text-sm font-medium">{lang.native_name}</span>
            <span className="block text-xs text-muted-foreground">
              {lang.display_name}
            </span>
          </span>
        </Label>
      ))}
    </RadioGroup>
  );
}
